import Helper from '@ember/component/helper';

import { action } from '@ember/object';
import { service } from '@ember/service';
import { getOwnConfig, importSync, macroCondition } from '@embroider/macros';

import { extentOne } from 'rise-ui/utils/array.js';
import { createCanvas as baseCreateCanvas } from 'rise-ui/utils/canvas.js';
import { alpha } from 'rise-ui/utils/color.js';
import { drawGraphLines } from 'rise-ui/utils/canvas/graph-lines.js';
import { drawGraphText } from 'rise-ui/utils/canvas/graph-text.js';
import { drawGraphAxis } from 'rise-ui/utils/canvas/graph-axis.js';
import { drawGraphLegend } from 'rise-ui/utils/canvas/graph-legend.js';
import { drawNodeSquare } from 'rise-ui/utils/canvas/graph-shapes.js';
import { call } from 'rise-ui/utils/function.js';
import { GraphConfig } from 'rise-ui/utils/graph-config.js';
import { isSet, isUnset } from 'rise-ui/utils/nullish.js';

const canvasToImage = macroCondition(!getOwnConfig().isSsrBuild)
  ? importSync('canvas-to-image').default : null;

const createCanvas = (width = null, height = null, backgroundColor = null) =>
  baseCreateCanvas(width, height, backgroundColor, null, 1);

export default class GraphDownloadHelper extends Helper {
  config = new GraphConfig();

  @service('debug/time-series-output') timeSeriesOutputs;

  @service userAgent;
  get isIOS() {
    return this.userAgent.os.isIOS;
  }

  compute(params, {
    title, disabled, filename,
    headings, footers,
    data, dataKey,
    dataKeyColors, dataKeyColorsCyclic,
    dataKeyLabels, dataKeyIndex, dataKeyCount,
    yLabel, yKey, xLabel, xKey,
    xTickAngle, tickFormat, tickMaxFormat, tickArguments,
    xLower, xUpper, yLower, yUpper,
    fontSize, fontColor, backgroundColor, lineColor, lineAlpha,
    legendRight, legendBottom,
  }) {
    const padding = 5;
    Object.assign(this.config.input, {
      data,
      headings, footers,
      yLabel, yKey, xLabel, xKey,
      xTickAngle, tickFormat, tickMaxFormat, tickArguments,
      fontSize, fontColor, lineColor, lineAlpha,
      legendRight, legendBottom,
      dataKeyColors, dataKeyColorsCyclic, dataKeyIndex, dataKeyLabels,
      paddingTop: padding, paddingRight: padding,
      paddingBottom: padding, paddingLeft: padding,
      legendGap: 10,
    });
    this.args = Object.assign(this.args || {}, {
      title, disabled, filename,
      dataKey, dataKeyCount, dataKeyColorsCyclic, dataKeyIndex,
      dataKeyColors, dataKeyLabels,
      xLower, xUpper, yLower, yUpper,
      backgroundColor,
    });
    return this;
  }

  draw() {
    const {
      dataKey: _dataKey, dataKeyCount, dataKeyColorsCyclic, dataKeyIndex,
      dataKeyColors: colorFn, dataKeyLabels: labelFn,
      xLower, xUpper, yLower, yUpper, backgroundColor,
    } = this.args;
    const { input, xAxisValue, yAxisValue } = this.config;
    let { width, height } = this.config;

    width = width || 0;
    height = height || 0;

    const canvas = createCanvas(width, height, backgroundColor);

    const data = [];
    let xDomain, yDomain;
    const dataKeyData = {};
    const dataKeyNodes = {};
    const dataKeyNulls = {};

    // Track data-keys (distinct graph lines), if any
    let dataKeys = new Set();

    // Map data to proxy objects and run accumulative operations
    (input.data || []).forEach((d, i) => {
      const xValue = xAxisValue(d, i);
      const yValue = yAxisValue(d, i);
      const dataKey = call(_dataKey, d[_dataKey], d, i);
      dataKeys.add(dataKey);

      xDomain = extentOne(xValue, xDomain);
      yDomain = extentOne(yValue, yDomain);

      let isNull = false;
      if (isUnset(xValue) || isUnset(yValue)) {
        isNull = true;
        if (!dataKeyNulls[dataKey]) {
          dataKeyNulls[dataKey] = [];
        }
        dataKeyNulls[dataKey].push(d);
      }

      if (!dataKeyData[dataKey]) {
        dataKeyData[dataKey] = [];
      }

      const allData = dataKeyData[dataKey];
      const nodes = dataKeyNodes[dataKey];
      const nulls = dataKeyNulls[dataKey];

      if (!isNull) {
        if (nulls && nulls[nulls.length - 1] === allData[allData.length - 1]) {
          // This is a node if last was a null
          if (!dataKeyNodes[dataKey]) {
            dataKeyNodes[dataKey] = [];
          }
          dataKeyNodes[dataKey].push(d);
        } else if (nodes && nodes[nodes.length - 1] === allData[allData.length - 1]) {
          // Last one is not longer a lone node if it can connect to this one
          dataKeyNodes[dataKey].pop();
        }
      }

      allData.push(d);

      const datum = Object.assign({}, {
        dataKey,
        xValue,
        yValue,
      });
      data.push(datum);
    });

    dataKeys = Array.from(dataKeys);
    input.dataKeys = dataKeys;

    xDomain = [ xLower ?? xDomain[0], xUpper ?? xDomain[1] ];
    yDomain = [ yLower ?? yDomain[0], yUpper ?? yDomain[1] ];

    // Set accumulated values for dependant CPs (Note: some outputs change)
    input.xDomain = xDomain;
    input.yDomain = yDomain;

    const {
      lineColor, lineAlpha,
      xScale, xAxisOptions, xAxisLeft, xAxisTop, xAxisSize,
      yScale, yAxisOptions, yAxisLeft, yAxisTop, yAxisSize,
      graphWidth, graphHeight, graphLeft, graphTop, nodeSize, graphMargin,
      headerOptions, headerHeight, headerLeft, headerTop,
      footerOptions, footerHeight, footerLeft, footerTop,
      legendRight, legendBottom, legendOptions,
    } = this.config;
    const singleNodeSize = nodeSize / 2;
    const singleNodeCenter = singleNodeSize / 2;

    const newX = ({ xValue }) => isSet(xValue) ? xScale(xValue) : xValue;
    const newY = ({ yValue }) => isSet(yValue) ? yScale(yValue) : yValue;

    data.forEach(d => {
      d.x = newX;
      d.y = newY;
    });

    const context = canvas.getContext('2d');

    const drawnLineNodes = {};
    const drawnNodes = {};
    const max = (dataKeyCount || dataKeys.length)
      - (dataKeyColorsCyclic ? 0 : 1) || 1;
    const indexFn = dataKeyIndex ? dataKey => {
        const index = dataKeyIndex(dataKey);
        return index >= 0 ? index : dataKeys.indexOf(dataKey)
      } : dataKey => dataKeys.indexOf(dataKey);

    let hasLastNode = false;
    data.forEach((d, i) => {
      const x = call(d.x, d.x, d, i);
      const y = call(d.y, d.y, d, i);
      const isNode = isSet(y);

      if (!isNode && !hasLastNode) {
        return;
      }

      const dataKey = d.dataKey;
      const nextDatum = data[i + 1]
      const nextY = nextDatum?.y
      const isNextYValueSet = i < data.length - 1
        && isSet(call(nextY, nextY, nextDatum, i + 1));
      if (isNode && !(hasLastNode || isNextYValueSet)) {
        if (!drawnNodes[dataKey]) {
          drawnNodes[dataKey] = [];
        }
        drawnNodes[dataKey].push({ x, y });
        return;
      }

      hasLastNode = isNode;

      const index = indexFn(dataKey);
      let label;
      if (dataKeys.length > 1 && (colorFn || labelFn)) {
        if (labelFn) {
          label = labelFn(dataKey, index);
        }
      }
      if (!drawnLineNodes[dataKey]) {
        drawnLineNodes[dataKey] = [];
        drawnLineNodes[dataKey].index = index;
      }

      drawnLineNodes[dataKey].push({ x, y, label });
    });

    const ctx = c => c.getContext('2d');

    const { colorGraph } = this.timeSeriesOutputs;

    if (xAxisSize.width && xAxisSize.height) {
      const bottomAxis = createCanvas(xAxisSize.width, xAxisSize.height, colorGraph?.xAxisBgColor);
      drawGraphAxis(ctx(bottomAxis), xScale, xAxisOptions);
      context.drawImage(bottomAxis, xAxisLeft, xAxisTop);
    }

    if (yAxisSize.width && yAxisSize.height) {
      const leftAxis = createCanvas(yAxisSize.width, yAxisSize.height, colorGraph?.yAxisBgColor);
      drawGraphAxis(ctx(leftAxis), yScale, yAxisOptions);
      context.drawImage(leftAxis, yAxisLeft, yAxisTop);
    }

    if (graphWidth && graphHeight) {
      const graph = createCanvas(graphWidth, graphHeight, colorGraph?.graphBgColor);
      const graphContext = ctx(graph);

      const scaleBy = (value, scale) => isSet(value) ? scale(value) : value;
      const transform = (d, i) => ({
        x: scaleBy(xAxisValue(d, i), xScale),
        y: scaleBy(yAxisValue(d, i), yScale),
      });

      // For each dataKey
      Object.entries(drawnLineNodes)
        // Reverse ordered since last is drawn over first
        .sort(([ , { index: a }], [ , { index: b }]) => b - a)
        .forEach(([ dataKey, value ]) => {
          const index = indexFn(dataKey);
          const colorValue = index / max;
          let c = lineColor;
          if (dataKeys.length > 1 && colorFn) {
            c = colorFn(colorValue);
          }

          drawGraphLines(graphContext, dataKeyData[dataKey], {
            padding: graphMargin,
            strokeStyle: lineAlpha && lineAlpha !== 1 ? alpha(c, lineAlpha) : c,
            transform,
          });
          graphContext.fillStyle = alpha(c, .5);
          graphContext.strokeStyle = 'rgba(0, 0, 0, 1)';
          const nodes = dataKeyNodes[dataKey] || [];
          for (let i = 0; i < nodes.length; ++i) {
            const { x, y } = transform(nodes[i], i);
            drawNodeSquare(
              graphContext,
              x + graphMargin - singleNodeCenter,
              y + graphMargin - singleNodeCenter,
              singleNodeSize
            );
          }
        });
      context.drawImage(graph, graphLeft, graphTop);
    }

    if (headerHeight) {
      const header = createCanvas(width, headerHeight);
      drawGraphText(ctx(header), headerOptions);
      context.drawImage(header, headerLeft || 0, headerTop || 0);
    }
    if (footerHeight) {
      const footer = createCanvas(width, footerHeight);
      drawGraphText(ctx(footer), footerOptions);
      context.drawImage(footer, footerLeft || 0, footerTop || 0);
    }
    if ((legendRight || legendBottom) && legendOptions.width && legendOptions.height) {
      const { legendKeys } = this.config;
      const legend = createCanvas(legendOptions.width, legendOptions.height, colorGraph?.legendBgColor);
      drawGraphLegend(ctx(legend), legendKeys, legendOptions);
      context.drawImage(legend,
        this.config[legendRight ? 'legendRightLeft' : 'legendBottomLeft'] || 0,
        this.config[legendRight ? 'legendRightTop' : 'legendBottomTop'] || 0
      );
    }

    return canvas;
  }

  download(canvas) {
    let type = 'png';
    let quality = 1;
    // iOS doesn't allow viewing downloaded PNG without complicated steps
    if (this.isIOS) {
      type = 'jpg';
      quality = 0.92;
    }
    canvasToImage(canvas, {
      name: this.args.filename,
      type: type,
      quality: quality
    });
  }

  @action
  onSelection(size) {
    if (!size) {
      return;
    }
    const [ width, height ] = size.split(/\s*,\s*/);
    const { input } = this.config;
    input.width = +width;
    input.height = +height;
    input.fontMultiplier
      = Math.max(1, Math.max(width, height) / 1200);
    this.download(this.draw());
  }
}
